import { useEffect, useState } from 'react';
import { MapPin, Utensils, ShoppingBag, Droplets, Users, Clock, X, Croissant, Beef, Sparkles, Filter, ChevronDown, Map, Navigation } from 'lucide-react';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import { DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';
import { useSearchPlaces } from '@/hooks/useSearchPlaces';
import { PlaceSummary } from '@/hooks/usePlaces';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { SearchFilters } from './SearchFilters';
import { useMapStore } from '@/store/mapStore';
import { cn } from '@/lib/utils';

interface SearchModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onPlaceSelect: (place: PlaceSummary) => void;
}

import React from 'react';

const MagenDavid = React.forwardRef<SVGSVGElement, { className?: string }>(
  ({ className }, ref) => (
    <svg ref={ref} viewBox="0 0 24 24" fill="currentColor" className={className}>
      <path d="M12 2L9.19 8H2.77L8.39 12L6.19 18.5L12 14.47L17.81 18.5L15.61 12L21.23 8H14.81L12 2Z" />
    </svg>
  )
);
MagenDavid.displayName = 'MagenDavid';

const categoryIcons: Record<string, React.ReactNode> = {
  synagogue: <MagenDavid className="w-4 h-4" />,
  restaurant: <Utensils className="w-4 h-4" />,
  grocery: <ShoppingBag className="w-4 h-4" />,
  bakery: <Croissant className="w-4 h-4" />,
  butcher: <Beef className="w-4 h-4" />,
  mikvah: <Droplets className="w-4 h-4" />,
  community: <Users className="w-4 h-4" />,
};

const categoryLabels: Record<string, string> = {
  synagogue: 'Synagogues',
  restaurant: 'Restaurants',
  grocery: 'Épiceries',
  bakery: 'Boulangeries',
  butcher: 'Boucheries',
  mikvah: 'Mikvaot',
  community: 'Communautés',
};

const formatDistance = (distanceKm: number | null | undefined): string | null => {
  if (distanceKm === null || distanceKm === undefined) return null;
  if (distanceKm < 1) {
    return `${Math.round(distanceKm * 1000)}m`;
  }
  return `${distanceKm.toFixed(1)}km`;
};

export const SearchModal = ({ open, onOpenChange, onPlaceSelect }: SearchModalProps) => {
  const [showFilters, setShowFilters] = useState(false);
  const { activeCategories, activeKosherStatuses, activeKosherAuthorities, setCenter, setHighlightedPlaceId, userLocation, setIsLocating } = useMapStore();
  
  const { 
    query, 
    setQuery, 
    groupedResults, 
    isLoading, 
    hasResults, 
    recentSearches, 
    addToRecentSearches,
    clearRecentSearches,
    suggestions,
    smartSuggestions,
    isSuggestionsLoadingNearby,
  } = useSearchPlaces();

  const handleViewOnMap = (place: PlaceSummary) => {
    if (place.lat && place.lng) {
      setCenter([place.lng, place.lat]);
    }
    // Set highlighted place for bounce animation
    setHighlightedPlaceId(place.id);
    onOpenChange(false);
  };

  const activeFiltersCount = activeCategories.length + activeKosherStatuses.length + activeKosherAuthorities.length;

  useEffect(() => {
    if (!open) {
      setQuery('');
      setShowFilters(false);
    }
  }, [open, setQuery]);

  // Global keyboard shortcut
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onOpenChange(true);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onOpenChange]);

  const handleSelect = (place: PlaceSummary) => {
    addToRecentSearches(place);
    onPlaceSelect(place);
    onOpenChange(false);
  };

  const handleCategoryClick = (categoryLabel: string) => {
    setQuery(categoryLabel);
  };

  const handleSuggestionClick = (name: string) => {
    setQuery(name);
  };

  return (
    <CommandDialog open={open} onOpenChange={onOpenChange} shouldFilter={false}>
      <VisuallyHidden>
        <DialogTitle>Rechercher un lieu</DialogTitle>
        <DialogDescription>
          Recherchez des synagogues, restaurants, et autres lieux
        </DialogDescription>
      </VisuallyHidden>
      <CommandInput
        placeholder="Rechercher un lieu, adresse, ville..."
        value={query}
        onValueChange={setQuery}
      />
      
      {/* Filter Toggle */}
      <Collapsible open={showFilters} onOpenChange={setShowFilters} className="flex-shrink-0">
        <CollapsibleTrigger asChild>
          <button className="w-full flex items-center justify-between px-4 py-2 text-sm text-muted-foreground hover:bg-muted/50 transition-colors border-b border-border">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4" />
              <span>Filtres</span>
              {activeFiltersCount > 0 && (
                <Badge variant="secondary" className="bg-primary text-primary-foreground h-5 min-w-5 px-1.5">
                  {activeFiltersCount}
                </Badge>
              )}
            </div>
            <ChevronDown className={cn(
              "w-4 h-4 transition-transform duration-200",
              showFilters && "rotate-180"
            )} />
          </button>
        </CollapsibleTrigger>
        <CollapsibleContent>
          <SearchFilters />
        </CollapsibleContent>
      </Collapsible>

      <CommandList>
        {isLoading && (
          <CommandGroup heading="Recherche en cours...">
            <div className="space-y-2 px-2 py-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <div key={i} className="flex items-center gap-3 py-2">
                  <div className="w-10 h-10 rounded-full bg-muted relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-foreground/5 before:to-transparent" />
                  <div className="flex-1 space-y-2">
                    <div className="h-4 w-32 rounded bg-muted relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-foreground/5 before:to-transparent" />
                    <div className="h-3 w-24 rounded bg-muted relative overflow-hidden before:absolute before:inset-0 before:-translate-x-full before:animate-shimmer before:bg-gradient-to-r before:from-transparent before:via-foreground/5 before:to-transparent" />
                  </div>
                </div>
              ))}
            </div>
          </CommandGroup>
        )}
        
        {!isLoading && query && !hasResults && suggestions.length === 0 && (
          <CommandEmpty>Aucun résultat trouvé pour "{query}"</CommandEmpty>
        )}

        {/* Autocomplete suggestions */}
        {query && suggestions.length > 0 && !hasResults && (
          <CommandGroup heading={
            <div className="flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              <span>Suggestions</span>
            </div>
          }>
            {suggestions.map((suggestion) => (
              <CommandItem
                key={`suggestion-${suggestion.id}`}
                value={`suggestion-${suggestion.name}`}
                onSelect={() => handleSuggestionClick(suggestion.name)}
                className="flex items-center gap-3 py-2 cursor-pointer"
              >
                <div className="p-1.5 rounded-full bg-primary/10 text-primary">
                  {categoryIcons[suggestion.category] || <MapPin className="w-3 h-3" />}
                </div>
                <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                  <span className="font-medium truncate">{suggestion.name}</span>
                  <span className="text-xs text-muted-foreground">{suggestion.city}</span>
                </div>
              </CommandItem>
            ))}
          </CommandGroup>
        )}


        {/* Recent searches */}
        {!query && recentSearches.length > 0 && (
          <CommandGroup heading={
            <div className="flex items-center justify-between">
              <span>Recherches récentes</span>
              <Button 
                variant="ghost" 
                size="sm" 
                className="h-auto py-0.5 px-2 text-xs text-muted-foreground hover:text-foreground"
                onClick={(e) => {
                  e.stopPropagation();
                  clearRecentSearches();
                }}
              >
                <X className="w-3 h-3 mr-1" />
                Effacer
              </Button>
            </div>
          }>
            {recentSearches.map((place) => (
              <CommandItem
                key={place.id}
                value={`recent-${place.name} ${place.address} ${place.city}`}
                onSelect={() => handleSelect(place)}
                className="flex items-center gap-3 py-3 cursor-pointer"
              >
                <div className="p-2 rounded-full bg-muted text-muted-foreground">
                  <Clock className="w-4 h-4" />
                </div>
                <div className="flex flex-col gap-0.5 flex-1">
                  <span className="font-medium">{place.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {place.city}
                  </span>
                </div>
                <span className="text-primary">
                  {categoryIcons[place.category] || <MapPin className="w-4 h-4" />}
                </span>
              </CommandItem>
            ))}
          </CommandGroup>
        )}

        {/* Geolocation prompt when no location */}
        {!query.trim() && !userLocation && (
          <div className="flex flex-col items-center gap-3 py-6 px-4 text-center">
            <div className="p-3 rounded-full bg-primary/10">
              <Navigation className="w-6 h-6 text-primary" />
            </div>
            <div className="space-y-1">
              <p className="text-sm font-medium text-foreground">
                Activez la géolocalisation
              </p>
              <p className="text-xs text-muted-foreground">
                Pour des suggestions personnalisées basées sur votre position
              </p>
            </div>
            <Button
              size="sm"
              onClick={() => {
                setIsLocating(true);
                if (navigator.geolocation) {
                  navigator.geolocation.getCurrentPosition(
                    (position) => {
                      const { setUserLocation, setIsLocating, setLocationError } = useMapStore.getState();
                      setUserLocation([position.coords.longitude, position.coords.latitude]);
                      setIsLocating(false);
                      setLocationError(null);
                    },
                    (error) => {
                      const { setIsLocating, setLocationError } = useMapStore.getState();
                      setIsLocating(false);
                      setLocationError(error.message);
                    }
                  );
                }
              }}
              className="gap-2"
            >
              <Navigation className="w-4 h-4" />
              Autoriser la localisation
            </Button>
          </div>
        )}

        {/* Smart suggestions when no search query */}
        {!query.trim() && !isSuggestionsLoadingNearby && smartSuggestions.length > 0 && (
          <CommandGroup heading={
            <div className="flex items-center gap-2">
              <Sparkles className="w-3 h-3" />
              <span>Suggestions pour vous</span>
            </div>
          }>
            {smartSuggestions.slice(0, 10).map((place) => {
              const distance = formatDistance(place.distance_km);
              return (
                <CommandItem
                  key={`nearby-${place.id}`}
                  value={`nearby-${place.name} ${place.address} ${place.city}`}
                  onSelect={() => handleSelect(place)}
                  className={cn(
                    "flex items-start gap-3 py-3 cursor-pointer",
                    place.is_featured && "bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-l-2 border-amber-400"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-full mt-0.5",
                    place.is_featured 
                      ? "bg-gradient-to-br from-amber-500/20 to-yellow-500/20 text-amber-600 dark:text-amber-400" 
                      : "bg-primary/10 text-primary"
                  )}>
                    {categoryIcons[place.category] || <MapPin className="w-4 h-4" />}
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{place.name}</span>
                      {place.is_featured && (
                        <span className="text-[9px] font-medium bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full border border-amber-400/30 whitespace-nowrap">
                          ✨ Tu pourrais aimer
                        </span>
                      )}
                      {place.rating ? (
                        <span className="flex items-center gap-0.5 text-xs text-amber-500">
                          ★ {place.rating.toFixed(1)}
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{place.address}, {place.city}</span>
                    </span>
                  </div>
                  {distance && (
                    <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      {distance}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-primary hover:bg-primary/10 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOnMap(place);
                    }}
                  >
                    <Map className="w-4 h-4" />
                  </Button>
                </CommandItem>
              );
            })}
          </CommandGroup>
        )}

        {/* Search results grouped by category */}
        {!isLoading && Object.entries(groupedResults).map(([category, places]) => (
          <CommandGroup key={category} heading={categoryLabels[category] || category}>
            {places.map((place) => {
              const distance = formatDistance(place.distance_km);
              return (
                <CommandItem
                  key={place.id}
                  value={`${place.name} ${place.address} ${place.city}`}
                  onSelect={() => handleSelect(place)}
                  className={cn(
                    "flex items-start gap-3 py-3 cursor-pointer",
                    place.is_featured && "bg-gradient-to-r from-amber-500/10 to-yellow-500/5 border-l-2 border-amber-400"
                  )}
                >
                  <div className={cn(
                    "p-2 rounded-full mt-0.5",
                    place.is_featured 
                      ? "bg-gradient-to-br from-amber-500/20 to-yellow-500/20 text-amber-600 dark:text-amber-400" 
                      : "bg-primary/10 text-primary"
                  )}>
                    {categoryIcons[place.category] || <MapPin className="w-4 h-4" />}
                  </div>
                  <div className="flex flex-col gap-0.5 flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold truncate">{place.name}</span>
                      {place.is_featured && (
                        <span className="text-[9px] font-medium bg-gradient-to-r from-amber-500/20 to-yellow-500/20 text-amber-600 dark:text-amber-400 px-1.5 py-0.5 rounded-full border border-amber-400/30 whitespace-nowrap">
                          ✨ Tu pourrais aimer
                        </span>
                      )}
                      {place.rating ? (
                        <span className="flex items-center gap-0.5 text-xs text-amber-500">
                          ★ {place.rating.toFixed(1)}
                        </span>
                      ) : null}
                    </div>
                    <span className="text-xs text-muted-foreground flex items-center gap-1">
                      <MapPin className="w-3 h-3 flex-shrink-0" />
                      <span className="truncate">{place.address}, {place.city}</span>
                    </span>
                  </div>
                  {distance && (
                    <span className="text-[10px] font-medium text-primary bg-primary/10 px-1.5 py-0.5 rounded-full whitespace-nowrap flex-shrink-0">
                      {distance}
                    </span>
                  )}
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7 text-primary hover:bg-primary/10 flex-shrink-0"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleViewOnMap(place);
                    }}
                  >
                    <Map className="w-4 h-4" />
                  </Button>
                </CommandItem>
              );
            })}
          </CommandGroup>
        ))}
      </CommandList>
    </CommandDialog>
  );
};
